import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { Trophy } from 'lucide-react';
import { getUserState } from '../../utils/location';
import Avatar from '../../components/common/Avatar';

const LeaderboardView = () => {
  const { users, currentUser, posts, events, viewUserProfile } = useContext(AppContext);
  const [tab, setTab] = useState('active');
  const [timeFilter, setTimeFilter] = useState('all');

  const getLeaderboardData = () => {
    const activeUsers = users.filter(u => u.status === 'active' && u.isPremium);

    if (tab === 'active') {
      const cutoff = timeFilter === 'week' ? Date.now() - 7 * 86400000
        : timeFilter === 'month' ? Date.now() - 30 * 86400000 : 0;

      return activeUsers.map(user => {
        const userPosts = posts.filter(p => p.userId === user.id && (!cutoff || new Date(p.timestamp).getTime() >= cutoff));
        const userComments = posts.reduce((count, p) =>
          count + (p.comments || []).filter(c => c.userId === user.id && (!cutoff || new Date(c.timestamp).getTime() >= cutoff)).length, 0);
        const userEvents = events.filter(e => e.userId === user.id && (!cutoff || new Date(e.timestamp).getTime() >= cutoff));
        return { user, score: userPosts.length + userComments + userEvents.length * 2, metric: 'activities' };
      }).sort((a, b) => b.score - a.score).slice(0, 100);

    } else if (tab === 'handicap') {
      return activeUsers
        .filter(u => u.handicap != null && u.handicap > 0)
        .map(user => ({ user, score: user.handicap, metric: 'handicap' }))
        .sort((a, b) => a.score - b.score).slice(0, 100);

    } else {
      // Followers are derived from other users' `following` arrays — `user.followers` is no longer written.
      const followerCounts = new Map();
      users.forEach(u => (u.following || []).forEach(id => {
        followerCounts.set(id, (followerCounts.get(id) || 0) + 1);
      }));
      return activeUsers
        .map(user => ({ user, score: followerCounts.get(user.id) || 0, metric: 'followers' }))
        .sort((a, b) => b.score - a.score).slice(0, 100);
    }
  };

  const leaderboard = getLeaderboardData();
  const getRankDisplay = (i) => i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
  const getRankColor = (i) => i === 0 ? 'text-yellow-500' : i === 1 ? 'text-gray-400' : i === 2 ? 'text-orange-600' : 'text-white';

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
        <Trophy className="w-7 h-7 mr-2 text-[#16a34a]" />
        Leaderboard
      </h2>

      {/* Tabs */}
      <div className="flex border-b border-gray-700 mb-4">
        {[['active', 'Most Active'], ['handicap', 'Top Handicap'], ['popular', 'Most Popular']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${tab === key ? 'text-white border-b-2 border-[#16a34a] font-bold' : 'text-gray-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Time filter for Most Active */}
      {tab === 'active' && (
        <div className="flex space-x-2 mb-4">
          {[['week', 'This Week'], ['month', 'This Month'], ['all', 'All Time']].map(([key, label]) => (
            <button key={key} onClick={() => setTimeFilter(key)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${timeFilter === key ? 'bg-[#16a34a] text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Leaderboard List */}
      <div className="space-y-2">
        {leaderboard.length === 0 ? (
          <div className="bg-black rounded-lg border border-gray-700 p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-400">No data available</p>
          </div>
        ) : leaderboard.map((entry, i) => (
          <div key={entry.user.id} className={`bg-black rounded-lg border p-4 flex items-center justify-between hover:border-[#16a34a] transition-colors ${entry.user.id === currentUser.id ? 'border-[#16a34a]' : 'border-gray-700'}`}>
            <div className="flex items-center space-x-4">
              <div className={`text-2xl font-bold ${getRankColor(i)} w-12 text-center`}>{getRankDisplay(i)}</div>
              <Avatar user={entry.user} size="md" />
              <div>
                <button onClick={() => viewUserProfile(entry.user.id)} className="font-bold text-white hover:text-[#16a34a] text-left">{entry.user.name}{getUserState(entry.user.location) && <span className="font-normal text-gray-400"> · {getUserState(entry.user.location)}</span>}</button>
                <p className="text-sm text-gray-400">@{entry.user.handle}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#16a34a]">{entry.score}</p>
              <p className="text-xs text-gray-400 capitalize">{entry.metric}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeaderboardView;
